import type { NotificationContent } from "../../../components/notifications/notification-content.types"

interface NotificationActor {
  id: string
  name: string
  email: string
  app_label: string
  type: string
}

interface NotificationTarget {
  id: string
  name: string
  email?: string | null
  app_label: string
  type: string
}

interface NotificationData {
  // AI_ESTIMATE_READY / AI_ESTIMATE_FAILED: the estimate and the wizard step
  // to open it on ("findings" or "details").
  estimate_id?: string
  estimate_step?: string
  lead_id?: string
  lead_name?: string
  new_status_label?: string
  old_status_label?: string
  // PROJECT_CREATED (ProjectNotificationService.notify_project_created)
  project_id?: string
  project_name?: string
  // Present on CALENDAR_EVENT_REMINDER (see calendars/notifications.py's
  // `_notify` helper) regardless of whether `target` itself is recognized —
  // the reliable way to build the calendar deep link.
  event_id?: string
  start_at?: string
  // Nullable on the model, and a newer WS field than start_at — absent on an
  // older backend or an event with no end time set.
  end_at?: string | null
  // LEAD_CUSTOMER_UPLOADED_FILES: the From Customer folder, and whether the
  // lead was already a project when the customer uploaded.
  folder_id?: string | null
  is_project?: boolean
  count?: number
  // Messaging (MESSAGE_DIRECT / MESSAGE_CUSTOMER / MESSAGE_MENTION): enough
  // to open the exact conversation at the exact message, which is what the
  // notification promises.
  conversation_id?: string
  conversation_kind?: "project_team" | "direct" | "customer"
  message_id?: string
  preview?: string
  // Subcontractor compliance expiry (SUBCONTRACTOR_DOCUMENT_EXPIRING): which
  // document lapsed, so the link can open that row's Upload/Replace action
  // rather than the profile's default tab. Written by `_notify_one` in
  // ezbuild_back/apps/subcontractor/notifications.py.
  document_type?: string
  expiration_date?: string
  // E-signature (900–906): the request, its record and who it was about.
  envelope_id?: string
  document_name?: string
  recipient_name?: string
  // Documents and file uploads: the attachment the row is about, and the
  // review it belongs to when there is one.
  attachment_id?: string
  review_id?: string
  file_name?: string
  // Finance: the record and the figures a financial alert compared.
  invoice_id?: string
  invoice_number?: string
  invoice_ids?: string[]
  invoice_numbers?: string[]
  milestone_id?: string
  milestone_ids?: string[]
  milestone_names?: string[]
  rule?: string
  basis?: "planned" | "actual"
  side?: "receivable" | "payable"
  severity?: string
  amount?: string | number
  contract_value?: string | number
  difference?: string | number
  direction?: "above" | "below"
  // A move a status rule made (`status_automation`): the transition behind it
  // and how long it can be undone.
  automation?: {
    rule: string
    signal_id: string
    transition_id: string | null
    undo_available_until: string | null
  }
}

type NotificationLevel = "success" | "info" | "warning" | "error" | number

const NOTIFICATION_TYPE = {
  PERMISSIONS: "permissions",
  // NotificationType.CALENDAR_EVENT_REMINDER = 111 (ezbuild_back
  // notifications_app/models.py). `type` (below) is the broad category
  // string ("calendar", "lead", ...) shared by EVERY notification of that
  // kind — a reminder, an assignment, a reschedule all carry `type:
  // "calendar"` — so this numeric code can only ever be compared against
  // `notification_name`, never `type`. Confirmed live: a real reminder's
  // `type` is literally the string "calendar", not 111 — a wrong assumption
  // here previously made the reminder toast/desktop-notification code an
  // unreachable branch against the real backend, passing only in unit tests
  // whose mock fixture set the wrong field's value.
  CALENDAR_EVENT_REMINDER: 111,
  // NotificationType.LEAD_CUSTOMER_UPLOADED_FILES — a customer sent files
  // through a meeting's upload link.
  LEAD_CUSTOMER_UPLOADED_FILES: 9,
  // Messaging. Their `target` is a Conversation, which no `target.type` case
  // below recognises — the link is built from `data` instead.
  MESSAGE_DIRECT: 800,
  MESSAGE_CUSTOMER: 801,
  MESSAGE_MENTION: 802
} as const

type NotificationTypeKeys = keyof typeof NOTIFICATION_TYPE
type NotificationTypes = (typeof NOTIFICATION_TYPE)[NotificationTypeKeys]

interface Notification {
  id: number
  level: NotificationLevel
  verb: string
  description: string
  unread: boolean
  timestamp: string
  type?: NotificationTypes
  // The specific notification code (e.g. 111) — see NOTIFICATION_TYPE above
  // for why `type` cannot answer "which kind of calendar notification is
  // this". Not every notification sets it on the backend (only calendar-
  // category ones currently do), hence optional/nullable.
  notification_name?: number | null
  // Whether the row's own actor is a customer — not derivable from `type`
  // or `notification_name` alone, since a type like PROJECT_FILE_UPLOADED
  // fires for a staff upload just as often as a customer's. Absent on an
  // older backend.
  is_customer_activity?: boolean
  timesince: string
  actor: NotificationActor
  target: NotificationTarget
  data: NotificationData
  // Absent on an older backend.
  section?: NotificationSection
  group_count?: number
  // Sent only when the row stands for more than one update.
  group_members?: Notification[]
  // The facts the row is about — the record, its customer, the
  // milestone/invoice/document, the amounts and dates, and one destination.
  // Resolved per viewer by `notifications_app/content.py`. Absent on a
  // backend that predates it, so every reader falls back through
  // `helpers/legacyContent`.
  content?: NotificationContent | null
}

const NOTIFICATION_SECTION = {
  NEEDS_ATTENTION: "needs_attention",
  RECENT_UPDATES: "recent_updates"
} as const

type NotificationSection =
  (typeof NOTIFICATION_SECTION)[keyof typeof NOTIFICATION_SECTION]

type NotificationCursor = string | number | null

const WS_MESSAGE_TYPE = {
  INITIAL_STATE: "initial_state",
  NOTIFICATION_NEW: "notification.new",
  NOTIFICATIONS_BATCH: "notifications_batch",
  GET_ALL_MESSAGES: "get_all_messages",
  ERROR: "error",
  MARK_READ: "mark_read",
  MARK_READ_SUCCESS: "mark_read_success",
  BADGE_UPDATE: "notification.badge_update",
  FETCH_MORE: "fetch_more",
  ARCHIVE: "archive",
  ARCHIVE_SUCCESS: "archive_success",
  ARCHIVE_ALL_READ: "archive_all_read",
  ARCHIVE_ALL_READ_SUCCESS: "archive_all_read_success",
  // No longer offered in the interface; installed builds still send it.
  DELETE: "delete",
  PING: "ping"
} as const

type WSMessageTypeKeys = keyof typeof WS_MESSAGE_TYPE
type WSMessageType = (typeof WS_MESSAGE_TYPE)[WSMessageTypeKeys]

interface NotificationCountsPayload {
  all?: number
  unread?: number
  needs_attention?: number
}

interface WSMessage {
  type: WSMessageType
  notifications?: Notification[]
  notification?: Notification
  unread_count?: number
  // Each filter chip's own count, counted the way its list is. Absent on a
  // backend that predates the filters.
  counts?: NotificationCountsPayload
  // The narrowing the server applied, echoed so a page answering a filter
  // the user has moved on from can be discarded.
  filters?: Record<string, unknown>
  has_more?: boolean
  // An opaque position, not a row id. Older servers still send a number.
  next_cursor: NotificationCursor
}

interface NotificationSettingsItem {
  notification_type: number
  notification_type_name: string
  email: boolean
  in_app: boolean
  phone: boolean
  desktop: boolean
  // Absent from servers that predate the channel; such a row never shows,
  // sends or clears it.
  whatsapp?: boolean
  sms?: boolean
  sms_available?: boolean
}

interface NotificationSettings {
  [key: string]: NotificationSettingsItem[]
}

export { WS_MESSAGE_TYPE, NOTIFICATION_TYPE, NOTIFICATION_SECTION }

export type {
  NotificationCountsPayload,
  WSMessageType,
  NotificationLevel,
  NotificationSection,
  NotificationCursor,
  Notification,
  WSMessage,
  NotificationSettings,
  NotificationSettingsItem
}
